// src/component/CouponAdminPage.js
// 公演ごとのクーポン管理ページ
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  collection,
  onSnapshot,
  addDoc,
  doc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  where,
  serverTimestamp,
  Timestamp,
  increment,
} from "firebase/firestore";
import { auth, db } from "../api/firebase";
import {
  Box,
  Button,
  Typography,
  Paper,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  Stack,
  Chip,
  IconButton,
  Divider,
  Alert,
  Collapse,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
} from "@mui/material";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import AccountTreeRoundedIcon from "@mui/icons-material/AccountTreeRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import BarChartRoundedIcon from "@mui/icons-material/BarChartRounded";
import ViewListRoundedIcon from "@mui/icons-material/ViewListRounded";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import { AppThemeContext } from "./AppShell";

// ─── helpers ──────────────────────────────────────
const COLLECTION = "coupons";
const DEFAULT_DISCOUNT_PERCENT = 50; // % 値（50 = 半額）

/** Firestore Timestamp → "YYYY-MM-DDTHH:mm" */
const toLocalInput = (v) => {
  if (!v) return "";
  const d = typeof v.toDate === "function" ? v.toDate() : new Date(v);
  if (isNaN(d.getTime())) return "";
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
};

/** "YYYY-MM-DDTHH:mm" → Firestore Timestamp */
const toTimestamp = (s) => (s ? Timestamp.fromDate(new Date(s)) : null);

/** 日付表示用 */
const fmtDate = (v) => {
  if (!v) return "—";
  const d = typeof v.toDate === "function" ? v.toDate() : new Date(v);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("ja-JP", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

// ─── component ────────────────────────────────────
export default function CouponAdminPage() {
  const navigate = useNavigate();
  const theme = React.useContext(AppThemeContext) || {};
  const accent = theme.railActive ?? "#5A35FF";
  const textBase = theme.railText ?? "rgba(40,40,60,0.9)";
  const border = theme.bottomBorder ?? "rgba(0,0,0,0.08)";
  const surface = theme.surfaceBg ?? "#FFF";

  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [coupons, setCoupons] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const [flowOpen, setFlowOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editTab, setEditTab] = useState(0); // 0: 編集, 1: 使用者
  const [couponUsers, setCouponUsers] = useState([]);
  const [couponUsersLoading, setCouponUsersLoading] = useState(false);
  const [revoking, setRevoking] = useState(null);
  const [usersViewMode, setUsersViewMode] = useState("list"); // "list" or "chart"

  const [form, setForm] = useState({
    eventName: "",
    code: "",
    discountPercent: DEFAULT_DISCOUNT_PERCENT,
    stripePromotionCodeId: "",
    start: "",
    end: "",
    maxUses: "",
    memo: "",
  });

  // ─── 管理者チェック ───
  useEffect(() => {
    const unsub = auth.onAuthStateChanged(async (user) => {
      if (!user) return navigate("/welcome");
      const snap = await getDoc(doc(db, "users", user.uid));
      if (snap.exists() && snap.data().isAdmin) {
        setIsAdmin(true);
      } else {
        navigate("/home");
      }
    });
    return unsub;
  }, [navigate]);

  // ─── リアルタイム取得 ───
  useEffect(() => {
    if (!isAdmin) return;
    const unsub = onSnapshot(
      collection(db, COLLECTION),
      (snap) => {
        const list = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .sort((a, b) => {
            const ta = a.createdAt?.toMillis?.() ?? 0;
            const tb = b.createdAt?.toMillis?.() ?? 0;
            return tb - ta;
          });
        setCoupons(list);
        setLoading(false);
      },
      (err) => {
        console.error("クーポン取得エラー:", err);
        setLoading(false);
      }
    );
    return unsub;
  }, [isAdmin]);

  // ─── 新規作成 ───
  const handleCreate = async () => {
    if (!form.eventName.trim() || !form.code.trim() || !form.stripePromotionCodeId.trim()) return;
    const pct = Number(form.discountPercent);
    await addDoc(collection(db, COLLECTION), {
      eventName: form.eventName.trim(),
      code: form.code.trim().toUpperCase(),
      discountPercent:
        Number.isFinite(pct) && pct >= 0 && pct <= 100
          ? pct
          : DEFAULT_DISCOUNT_PERCENT,
      stripePromotionCodeId: form.stripePromotionCodeId.trim(),
      start: toTimestamp(form.start),
      end: toTimestamp(form.end),
      maxUses: form.maxUses ? Number(form.maxUses) : null,
      usedCount: 0,
      active: true,
      memo: form.memo.trim(),
      createdAt: serverTimestamp(),
    });
    setDialogOpen(false);
    setForm({
      eventName: "",
      code: "",
      discountPercent: DEFAULT_DISCOUNT_PERCENT,
      stripePromotionCodeId: "",
      start: "",
      end: "",
      maxUses: "",
      memo: "",
    });
  };

  // ─── 有効/無効切替 ───
  const toggleActive = async (coupon) => {
    await updateDoc(doc(db, COLLECTION, coupon.id), {
      active: !coupon.active,
    });
  };

  // ─── 編集 ───
  const handleEditOpen = (coupon) => {
    setEditTarget(coupon);
    setEditForm({
      eventName: coupon.eventName || "",
      code: coupon.code || "",
      discountPercent: coupon.discountPercent ?? DEFAULT_DISCOUNT_PERCENT,
      stripePromotionCodeId: coupon.stripePromotionCodeId || "",
      start: toLocalInput(coupon.start),
      end: toLocalInput(coupon.end),
      maxUses: coupon.maxUses ?? "",
      memo: coupon.memo || "",
    });
    setEditTab(0);
    setEditDialogOpen(true);
    loadCouponUsers(coupon.code);
  };

  // ─── 使用者取得 ───
  const loadCouponUsers = async (code) => {
    setCouponUsersLoading(true);
    setCouponUsers([]);
    try {
      // payments コレクションから該当クーポン使用者を取得
      const paySnap = await getDocs(
        query(collection(db, "payments"), where("couponCode", "==", code))
      );
      const users = [];
      for (const payDoc of paySnap.docs) {
        const pay = payDoc.data();
        // ユーザー名を取得
        let displayName = pay.userId;
        try {
          const userSnap = await getDoc(doc(db, "users", pay.userId));
          if (userSnap.exists()) {
            const u = userSnap.data();
            displayName = u.registrationName || u.displayName || pay.userId;
          }
        } catch (_) { /* ignore */ }
        users.push({
          paymentDocId: payDoc.id,
          userId: pay.userId,
          displayName,
          paymentMethod: pay.paymentMethod || "不明",
          amountTotal: pay.amountTotal,
          timestamp: pay.timestamp,
        });
      }

      // 銀行振込からも取得
      const bankSnap = await getDocs(
        query(collection(db, "bankTransferRequests2026"), where("couponCode", "==", code))
      );
      for (const bankDoc of bankSnap.docs) {
        const bank = bankDoc.data();
        if (bank.cancel) continue; // キャンセル済みはスキップ
        // 既にpaymentsで取得済みのユーザーは除外
        if (users.some((u) => u.userId === bank.userId)) continue;
        let displayName = bank.registrationName || bank.userId;
        users.push({
          bankTransferDocId: bankDoc.id,
          userId: bank.userId,
          displayName,
          paymentMethod: "銀行振込（申請中）",
          amountTotal: bank.amount,
          timestamp: bank.requestedAt,
        });
      }

      setCouponUsers(users);
    } catch (err) {
      console.error("使用者取得エラー:", err);
    }
    setCouponUsersLoading(false);
  };

  // ─── クーポン使用取消 ───
  const handleRevokeCouponUsage = async (user) => {
    if (!editTarget) return;
    if (!window.confirm(`${user.displayName} のクーポン使用を取り消しますか？\n\n・決済記録の削除\n・ユーザーの支払い履歴からの削除\n・使用回数の-1\n\nが行われます。`)) return;

    setRevoking(user.userId);
    try {
      // 1. payments ドキュメント削除
      if (user.paymentDocId) {
        await deleteDoc(doc(db, "payments", user.paymentDocId));
      }

      // 2. bankTransferRequests2026 のキャンセル
      if (user.bankTransferDocId) {
        await updateDoc(doc(db, "bankTransferRequests2026", user.bankTransferDocId), {
          cancel: true,
          canceledAt: serverTimestamp(),
        });
      }

      // 3. users/{uid} の payments 配列から該当エントリを削除
      const userRef = doc(db, "users", user.userId);
      const userSnap = await getDoc(userRef);
      if (userSnap.exists()) {
        const userData = userSnap.data();
        const updatedPayments = (userData.payments || []).filter(
          (p) => !(p.couponCode === editTarget.code && p.paymentPhase === "4")
        );
        await updateDoc(userRef, { payments: updatedPayments });
      }

      // 4. coupons の usedCount を -1
      await updateDoc(doc(db, COLLECTION, editTarget.id), {
        usedCount: increment(-1),
      });

      // リロード
      loadCouponUsers(editTarget.code);
    } catch (err) {
      console.error("取消エラー:", err);
      alert("取消に失敗しました: " + err.message);
    }
    setRevoking(null);
  };

  const handleEditSave = async () => {
    if (!editTarget) return;
    const pct = Number(editForm.discountPercent);
    await updateDoc(doc(db, COLLECTION, editTarget.id), {
      eventName: editForm.eventName.trim(),
      code: editForm.code.trim().toUpperCase(),
      discountPercent:
        Number.isFinite(pct) && pct >= 0 && pct <= 100
          ? pct
          : DEFAULT_DISCOUNT_PERCENT,
      stripePromotionCodeId: editForm.stripePromotionCodeId.trim(),
      start: toTimestamp(editForm.start),
      end: toTimestamp(editForm.end),
      maxUses: editForm.maxUses ? Number(editForm.maxUses) : null,
      memo: editForm.memo.trim(),
    });
    setEditDialogOpen(false);
    setEditTarget(null);
  };

  // ─── コードコピー ───
  const handleCopy = (code, id) => {
    navigator.clipboard.writeText(code);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  // ─── 有効期間の状態判定 ───
  const getStatus = (coupon) => {
    if (!coupon.active) return { label: "無効", color: "default" };
    const now = Date.now();
    const start = coupon.start?.toMillis?.() ?? 0;
    const end = coupon.end?.toMillis?.() ?? Infinity;
    if (now < start) return { label: "開始前", color: "info" };
    if (now > end) return { label: "期限切れ", color: "warning" };
    if (coupon.maxUses && coupon.usedCount >= coupon.maxUses)
      return { label: "上限到達", color: "warning" };
    return { label: "有効", color: "success" };
  };

  // ─── render ─────────────────────────────────────
  if (!isAdmin) return null;

  return (
    <Box sx={{ p: { xs: 2.5, sm: 3.5 }, maxWidth: 900, mx: "auto" }}>
      {/* ヘッダ */}
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="overline"
          sx={{ letterSpacing: "0.18em", fontSize: 11, opacity: 0.8, color: textBase }}
        >
          ADMIN
        </Typography>
        <Typography
          variant="h5"
          sx={{ fontWeight: 800, letterSpacing: "0.03em", color: textBase }}
        >
          クーポン管理
        </Typography>
        <Typography variant="body2" sx={{ mt: 0.5, color: "rgba(0,0,0,0.6)" }}>
          公演ごとのクーポンを作成・管理します。クーポンごとに通常価格と割引後価格を設定できます。
        </Typography>
      </Box>

      {/* 新規作成ボタン */}
      <Button
        variant="contained"
        startIcon={<AddCircleIcon />}
        onClick={() => setDialogOpen(true)}
        sx={{
          mb: 3,
          borderRadius: 2,
          textTransform: "none",
          fontWeight: 700,
          bgcolor: accent,
          "&:hover": { bgcolor: accent, opacity: 0.9 },
        }}
      >
        新しいクーポンを作成
      </Button>

      {/* ローディング */}
      {loading && (
        <Box sx={{ textAlign: "center", py: 6 }}>
          <CircularProgress size={32} />
        </Box>
      )}

      {/* クーポン一覧 */}
      {!loading && coupons.length === 0 && (
        <Alert severity="info" sx={{ borderRadius: 2 }}>
          クーポンがまだありません。「新しいクーポンを作成」から追加してください。
        </Alert>
      )}

      <Stack spacing={2}>
        {coupons.map((c) => {
          const status = getStatus(c);
          return (
            <Paper
              key={c.id}
              elevation={0}
              onClick={() => handleEditOpen(c)}
              sx={{
                p: 2.5,
                cursor: "pointer",
                "&:hover": { boxShadow: "0 4px 16px rgba(0,0,0,0.1)" },
                transition: "box-shadow .2s",
                borderRadius: 3,
                border: `1px solid ${border}`,
                bgcolor: surface,
                opacity: c.active ? 1 : 0.55,
              }}
            >
              <Stack
                direction={{ xs: "column", sm: "row" }}
                justifyContent="space-between"
                alignItems={{ sm: "center" }}
                spacing={1.5}
              >
                {/* 左側: 公演名 + コード */}
                <Box sx={{ minWidth: 0 }}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 700, color: textBase }}
                    >
                      {c.eventName}
                    </Typography>
                    <Chip label={status.label} color={status.color} size="small" />
                  </Stack>

                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: "monospace",
                        fontSize: 15,
                        fontWeight: 600,
                        letterSpacing: "0.12em",
                        color: accent,
                      }}
                    >
                      {c.code}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={(e) => { e.stopPropagation(); handleCopy(c.code, c.id); }}
                      sx={{ color: "rgba(0,0,0,0.4)" }}
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                    {copiedId === c.id && (
                      <Typography variant="caption" color="success.main">
                        コピーしました
                      </Typography>
                    )}
                  </Stack>

                  {c.memo && (
                    <Typography variant="caption" sx={{ color: "rgba(0,0,0,0.5)", mt: 0.5 }}>
                      {c.memo}
                    </Typography>
                  )}
                </Box>

                {/* 右側: 情報 + スイッチ */}
                <Stack direction="row" spacing={2} alignItems="center">
                  <Box sx={{ textAlign: "right" }}>
                    <Typography variant="body2" sx={{ color: "rgba(0,0,0,0.6)", fontSize: 12 }}>
                      <strong style={{ color: accent }}>
                        {c.discountPercent ?? DEFAULT_DISCOUNT_PERCENT}% OFF
                      </strong>
                    </Typography>
                    <Typography variant="body2" sx={{ color: "rgba(0,0,0,0.6)", fontSize: 12 }}>
                      使用: {c.usedCount ?? 0}
                      {c.maxUses ? ` / ${c.maxUses}` : ""} 回
                    </Typography>
                    <Typography variant="caption" sx={{ color: "rgba(0,0,0,0.45)", fontSize: 11 }}>
                      {fmtDate(c.start)} 〜 {fmtDate(c.end)}
                    </Typography>
                  </Box>

                  <Divider orientation="vertical" flexItem />

                  <Stack alignItems="center" spacing={0}>
                    <Switch
                      checked={!!c.active}
                      onChange={(e) => { e.stopPropagation(); toggleActive(c); }}
                      onClick={(e) => e.stopPropagation()}
                      color="success"
                      size="small"
                    />
                    <Typography variant="caption" sx={{ fontSize: 10, color: "rgba(0,0,0,0.5)" }}>
                      {c.active ? "有効" : "無効"}
                    </Typography>
                  </Stack>
                </Stack>
              </Stack>
            </Paper>
          );
        })}
      </Stack>

      {/* ─── ユーザー導線フローチャート ─── */}
      <Box sx={{ mt: 4 }}>
        <Button
          variant="text"
          startIcon={<AccountTreeRoundedIcon />}
          onClick={() => setFlowOpen(!flowOpen)}
          sx={{
            color: "rgba(0,0,0,0.55)",
            textTransform: "none",
            fontWeight: 600,
            fontSize: 13,
            "&:hover": { color: accent },
          }}
        >
          {flowOpen ? "ユーザー導線を閉じる" : "ユーザー導線を確認する"}
        </Button>

        <Collapse in={flowOpen}>
          <Paper
            elevation={0}
            sx={{
              mt: 1.5,
              p: 3,
              borderRadius: 3,
              border: `1px solid ${border}`,
              bgcolor: surface,
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: textBase, mb: 2 }}>
              クーポン利用フロー（ユーザー視点）
            </Typography>

            {/* フローチャート */}
            {[
              { step: "1", label: "決済ページを開く", desc: "トップページ / ヘッダー / ダイアログから遷移" },
              { step: "2", label: "価格表示: ¥55,000", desc: "通常価格が表示される" },
              { step: "3", label: "「クーポンコードをお持ちですか？」", desc: "価格の下にさりげなく表示。タップで入力欄が開く" },
              { step: "4", label: "クーポンコードを入力", desc: "公演で配布されたコード（例: NIIGATA0429）を入力" },
              { step: "5", label: "コード検証", desc: "Firestore で有効性・期限・使用上限をチェック" },
              { step: "6", label: "価格変更: ¥55,000 → ¥27,500", desc: "通常価格に取消線、割引価格を赤字で表示" },
              { step: "7", label: "支払い方法を選択", desc: "" },
            ].map((item) => (
              <Box key={item.step} sx={{ display: "flex", mb: 1.5 }}>
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: "50%",
                    bgcolor: `${accent}18`,
                    color: accent,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: 800,
                    fontSize: 13,
                    flexShrink: 0,
                    mr: 1.5,
                    mt: 0.2,
                  }}
                >
                  {item.step}
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: textBase }}>
                    {item.label}
                  </Typography>
                  {item.desc && (
                    <Typography variant="caption" sx={{ color: "rgba(0,0,0,0.5)" }}>
                      {item.desc}
                    </Typography>
                  )}
                </Box>
              </Box>
            ))}

            {/* 分岐 */}
            <Box sx={{ ml: 4.5, mt: 1, mb: 1 }}>
              <Divider sx={{ mb: 1.5 }} />
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                {/* クレカルート */}
                <Paper
                  elevation={0}
                  sx={{
                    flex: 1,
                    p: 2,
                    borderRadius: 2,
                    border: "1px solid rgba(25,118,210,0.3)",
                    bgcolor: "rgba(25,118,210,0.04)",
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#1976d2", mb: 1 }}>
                    クレジットカード
                  </Typography>
                  <Typography variant="caption" sx={{ color: "rgba(0,0,0,0.6)", display: "block" }}>
                    → Stripe決済画面へ
                  </Typography>
                  <Typography variant="caption" sx={{ color: "rgba(0,0,0,0.6)", display: "block" }}>
                    → Stripe側で50%OFF自動適用
                  </Typography>
                  <Typography variant="caption" sx={{ color: "rgba(0,0,0,0.6)", display: "block" }}>
                    → ¥27,500で決済完了
                  </Typography>
                  <Typography variant="caption" sx={{ color: "rgba(0,0,0,0.6)", display: "block" }}>
                    → 確認メール送信
                  </Typography>
                </Paper>

                {/* 銀行振込ルート */}
                <Paper
                  elevation={0}
                  sx={{
                    flex: 1,
                    p: 2,
                    borderRadius: 2,
                    border: "1px solid rgba(237,108,2,0.3)",
                    bgcolor: "rgba(237,108,2,0.04)",
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#ed6c02", mb: 1 }}>
                    銀行振込
                  </Typography>
                  <Typography variant="caption" sx={{ color: "rgba(0,0,0,0.6)", display: "block" }}>
                    → 確認ダイアログ（クーポン適用額表示）
                  </Typography>
                  <Typography variant="caption" sx={{ color: "rgba(0,0,0,0.6)", display: "block" }}>
                    → 振込申込 → 案内メール送信
                  </Typography>
                  <Typography variant="caption" sx={{ color: "rgba(0,0,0,0.6)", display: "block" }}>
                    → 振込金額: ¥27,500
                  </Typography>
                  <Typography variant="caption" sx={{ color: "rgba(0,0,0,0.6)", display: "block" }}>
                    → 管理者が入金確認後、権限付与
                  </Typography>
                </Paper>
              </Stack>

              <Divider sx={{ mt: 1.5, mb: 1.5 }} />
              <Typography variant="caption" sx={{ color: "rgba(0,0,0,0.5)" }}>
                決済完了後 → payments / users に couponCode を記録 → coupons の usedCount +1
              </Typography>
            </Box>
          </Paper>
        </Collapse>
      </Box>

      {/* ─── 新規作成ダイアログ ─── */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>新しいクーポンを作成</DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            <TextField
              label="公演名（イベント名）"
              placeholder="例: 東京講演会 2026/5/10"
              value={form.eventName}
              onChange={(e) => setForm({ ...form, eventName: e.target.value })}
              fullWidth
              required
            />

            <TextField
              label="クーポンコード"
              placeholder="例: SAPPORO0419"
              value={form.code}
              onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
              fullWidth
              required
              helperText="Stripeで作成したPromotion Codeと同じコード名を入力してください"
              InputProps={{ sx: { fontFamily: "monospace", letterSpacing: "0.15em" } }}
            />

            <TextField
              label="Stripe Promotion Code ID"
              placeholder="例: promo_xxxxxxxxxxxxx"
              value={form.stripePromotionCodeId}
              onChange={(e) => setForm({ ...form, stripePromotionCodeId: e.target.value })}
              fullWidth
              required
              helperText="Stripeダッシュボードで作成したPromotion CodeのIDを貼り付けてください"
              InputProps={{ sx: { fontFamily: "monospace", fontSize: 13 } }}
            />

            <TextField
              label="割引率（%）"
              type="number"
              value={form.discountPercent}
              onChange={(e) =>
                setForm({ ...form, discountPercent: e.target.value })
              }
              fullWidth
              required
              helperText="例: 50（半額）/ 10（10%OFF）。Stripe側のクーポン設定と一致させてください。"
              inputProps={{ min: 0, max: 100 }}
            />

            <Stack direction="row" spacing={2}>
              <TextField
                label="有効開始日時"
                type="datetime-local"
                value={form.start}
                onChange={(e) => setForm({ ...form, start: e.target.value })}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="有効終了日時"
                type="datetime-local"
                value={form.end}
                onChange={(e) => setForm({ ...form, end: e.target.value })}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Stack>

            <TextField
              label="利用上限回数（任意）"
              type="number"
              value={form.maxUses}
              onChange={(e) => setForm({ ...form, maxUses: e.target.value })}
              fullWidth
              helperText="空欄の場合は無制限"
            />

            <TextField
              label="メモ（管理者用）"
              value={form.memo}
              onChange={(e) => setForm({ ...form, memo: e.target.value })}
              fullWidth
              multiline
              rows={2}
              placeholder="例: 会場で配布するQRコード用"
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setDialogOpen(false)} sx={{ color: "rgba(0,0,0,0.6)" }}>
            キャンセル
          </Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={!form.eventName.trim() || !form.code.trim() || !form.stripePromotionCodeId.trim()}
            sx={{
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 700,
              bgcolor: accent,
              "&:hover": { bgcolor: accent, opacity: 0.9 },
            }}
          >
            作成する
          </Button>
        </DialogActions>
      </Dialog>

      {/* ─── 編集ダイアログ（タブ付き） ─── */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 0 }}>
          {editTarget?.eventName || "クーポン"}
        </DialogTitle>

        <Tabs
          value={editTab}
          onChange={(_, v) => setEditTab(v)}
          sx={{ px: 3, borderBottom: `1px solid ${border}` }}
        >
          <Tab label="編集" sx={{ textTransform: "none", fontWeight: 600 }} />
          <Tab
            label={`使用者${couponUsers.length ? ` (${couponUsers.length})` : ""}`}
            sx={{ textTransform: "none", fontWeight: 600 }}
          />
        </Tabs>

        <DialogContent sx={{ pt: 2 }}>
          {/* ── タブ0: 編集 ── */}
          {editTab === 0 && (
            <Stack spacing={2.5}>
              <TextField
                label="公演名（イベント名）"
                value={editForm.eventName || ""}
                onChange={(e) => setEditForm({ ...editForm, eventName: e.target.value })}
                fullWidth
                required
              />
              <TextField
                label="クーポンコード"
                value={editForm.code || ""}
                onChange={(e) => setEditForm({ ...editForm, code: e.target.value.toUpperCase() })}
                fullWidth
                required
                helperText="Stripeで作成したPromotion Codeと同じコード名"
                InputProps={{ sx: { fontFamily: "monospace", letterSpacing: "0.15em" } }}
              />
              <TextField
                label="Stripe Promotion Code ID"
                value={editForm.stripePromotionCodeId || ""}
                onChange={(e) => setEditForm({ ...editForm, stripePromotionCodeId: e.target.value })}
                fullWidth
                required
                InputProps={{ sx: { fontFamily: "monospace", fontSize: 13 } }}
              />
              <TextField
                label="割引率（%）"
                type="number"
                value={editForm.discountPercent ?? ""}
                onChange={(e) =>
                  setEditForm({ ...editForm, discountPercent: e.target.value })
                }
                fullWidth
                required
                helperText="例: 50（半額）/ 10（10%OFF）。Stripe側のクーポン設定と一致させてください。"
                inputProps={{ min: 0, max: 100 }}
              />
              <Stack direction="row" spacing={2}>
                <TextField
                  label="有効開始日時"
                  type="datetime-local"
                  value={editForm.start || ""}
                  onChange={(e) => setEditForm({ ...editForm, start: e.target.value })}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  label="有効終了日時"
                  type="datetime-local"
                  value={editForm.end || ""}
                  onChange={(e) => setEditForm({ ...editForm, end: e.target.value })}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Stack>
              <TextField
                label="利用上限回数（任意）"
                type="number"
                value={editForm.maxUses ?? ""}
                onChange={(e) => setEditForm({ ...editForm, maxUses: e.target.value })}
                fullWidth
                helperText="空欄の場合は無制限"
              />
              <TextField
                label="メモ（管理者用）"
                value={editForm.memo || ""}
                onChange={(e) => setEditForm({ ...editForm, memo: e.target.value })}
                fullWidth
                multiline
                rows={2}
              />
            </Stack>
          )}

          {/* ── タブ1: 使用者一覧 ── */}
          {editTab === 1 && (
            <Box>
              {couponUsersLoading ? (
                <Box sx={{ textAlign: "center", py: 4 }}>
                  <CircularProgress size={28} />
                </Box>
              ) : couponUsers.length === 0 ? (
                <Alert severity="info" sx={{ borderRadius: 2 }}>
                  このクーポンはまだ使用されていません。
                </Alert>
              ) : (
                <>
                  {/* 表示切替 */}
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ mb: 1.5 }}
                  >
                    <Typography variant="caption" sx={{ color: "rgba(0,0,0,0.55)" }}>
                      合計 {couponUsers.length} 名
                      {" / "}
                      決済済 {couponUsers.filter((u) => u.paymentMethod === "クレジットカード" || u.paymentMethod === "銀行振込").length} 名
                    </Typography>
                    <Stack direction="row" spacing={0.5}>
                      <IconButton
                        size="small"
                        onClick={() => setUsersViewMode("list")}
                        sx={{
                          color: usersViewMode === "list" ? accent : "rgba(0,0,0,0.4)",
                          bgcolor: usersViewMode === "list" ? `${accent}15` : "transparent",
                        }}
                      >
                        <ViewListRoundedIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => setUsersViewMode("chart")}
                        sx={{
                          color: usersViewMode === "chart" ? accent : "rgba(0,0,0,0.4)",
                          bgcolor: usersViewMode === "chart" ? `${accent}15` : "transparent",
                        }}
                      >
                        <BarChartRoundedIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </Stack>

                  {usersViewMode === "list" ? (
                    <List disablePadding>
                      {couponUsers.map((u, i) => (
                        <ListItem
                          key={u.userId + i}
                          divider={i < couponUsers.length - 1}
                          sx={{ px: 0 }}
                        >
                          <ListItemText
                            primary={
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {u.displayName}
                              </Typography>
                            }
                            secondary={
                              <Typography variant="caption" sx={{ color: "rgba(0,0,0,0.5)" }}>
                                {u.paymentMethod}
                                {u.amountTotal
                                  ? ` / ¥${(typeof u.amountTotal === "number" && u.amountTotal > 1000
                                      ? u.amountTotal / 100
                                      : u.amountTotal
                                    ).toLocaleString()}`
                                  : ""}
                                {u.timestamp
                                  ? ` / ${fmtDate(u.timestamp)}`
                                  : ""}
                              </Typography>
                            }
                          />
                          <ListItemSecondaryAction>
                            <IconButton
                              edge="end"
                              color="error"
                              size="small"
                              disabled={revoking === u.userId}
                              onClick={() => handleRevokeCouponUsage(u)}
                              title="使用を取り消す"
                            >
                              <DeleteOutlineRoundedIcon fontSize="small" />
                            </IconButton>
                          </ListItemSecondaryAction>
                        </ListItem>
                      ))}
                    </List>
                  ) : (
                    // チャートビュー
                    <Box>
                      {(() => {
                        // 日別集計
                        const byDate = {};
                        couponUsers.forEach((u) => {
                          const d = u.timestamp?.toDate?.() ?? (u.timestamp ? new Date(u.timestamp) : null);
                          if (!d || isNaN(d.getTime())) return;
                          const key = `${d.getMonth() + 1}/${d.getDate()}`;
                          byDate[key] = (byDate[key] || 0) + 1;
                        });
                        const chartData = Object.entries(byDate)
                          .map(([date, count]) => ({ date, count }))
                          .sort((a, b) => {
                            const [ma, da] = a.date.split("/").map(Number);
                            const [mb, db] = b.date.split("/").map(Number);
                            return ma * 100 + da - (mb * 100 + db);
                          });

                        if (chartData.length === 0) {
                          return (
                            <Alert severity="info" sx={{ borderRadius: 2 }}>
                              日付情報がある使用者がいません。
                            </Alert>
                          );
                        }

                        return (
                          <>
                            <Box sx={{ width: "100%", height: 260, mb: 1.5 }}>
                              <ResponsiveContainer>
                                <BarChart data={chartData}>
                                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.08)" />
                                  <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                                  <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                                  <Tooltip
                                    contentStyle={{
                                      borderRadius: 8,
                                      border: "1px solid rgba(0,0,0,0.1)",
                                      fontSize: 12,
                                    }}
                                    formatter={(v) => [`${v}名`, "使用"]}
                                  />
                                  <Bar dataKey="count" fill={accent} radius={[4, 4, 0, 0]} />
                                </BarChart>
                              </ResponsiveContainer>
                            </Box>
                            <Divider sx={{ mb: 1 }} />
                            <Stack spacing={0.5}>
                              {chartData.map((d) => (
                                <Stack
                                  key={d.date}
                                  direction="row"
                                  justifyContent="space-between"
                                >
                                  <Typography variant="caption" sx={{ color: "rgba(0,0,0,0.65)" }}>
                                    {d.date}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    sx={{ fontWeight: 700, color: accent }}
                                  >
                                    {d.count} 名
                                  </Typography>
                                </Stack>
                              ))}
                            </Stack>
                          </>
                        );
                      })()}
                    </Box>
                  )}
                </>
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setEditDialogOpen(false)} sx={{ color: "rgba(0,0,0,0.6)" }}>
            閉じる
          </Button>
          {editTab === 0 && (
            <Button
              variant="contained"
              onClick={handleEditSave}
              disabled={!editForm.eventName?.trim() || !editForm.code?.trim() || !editForm.stripePromotionCodeId?.trim()}
              sx={{
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 700,
                bgcolor: accent,
                "&:hover": { bgcolor: accent, opacity: 0.9 },
              }}
            >
              保存する
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}
